import { Card, CardContent, CardMedia, Chip, Container, Grid, Pagination, Stack } from "@mui/material";
import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";
import SEO from "components/SEO";
import { articles } from "data/articles";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

function BlogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const articlesPerPage = 9;

  useEffect(() => {
    const category = searchParams.get("category");
    const pageParam = parseInt(searchParams.get("page")) || 1;
    setSelectedCategory(category);
    setPage(pageParam);
  }, [searchParams]);

  const categories = [
    { 
      name: "All", 
      slug: null,
      image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      description: "Expert articles on AI fitness, workout plans, and training strategies"
    },
    { 
      name: "AI Fitness", 
      slug: "ai-fitness",
      image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      description: "Discover how AI transforms workout planning and fitness optimization"
    },
    { 
      name: "Workout Plans", 
      slug: "workout-plans",
      image: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      description: "Structured training routines and programs for every fitness level"
    },
    { 
      name: "Home Workouts", 
      slug: "home-workouts",
      image: "https://images.unsplash.com/photo-1584735935682-2f2b69dff9d2?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      description: "Effective exercises you can do anywhere without equipment"
    },
    { 
      name: "Weight Loss", 
      slug: "weight-loss",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      description: "Science-backed strategies for sustainable fat loss and body transformation"
    },
  ];

  // Filter articles by category
  const filteredArticles = selectedCategory
    ? articles.filter((article) => article.category === selectedCategory)
    : articles;

  // Pagination logic
  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);
  const startIndex = (page - 1) * articlesPerPage;
  const paginatedArticles = filteredArticles.slice(startIndex, startIndex + articlesPerPage);

  const handleCategoryChange = (categorySlug) => {
    setPage(1);
    const params = {};
    if (categorySlug) params.category = categorySlug;
    setSearchParams(params);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    const params = {};
    if (selectedCategory) params.category = selectedCategory;
    if (value > 1) params.page = value;
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentCategory = categories.find((c) => c.slug === selectedCategory) || categories[0];
  
  const seoTitle = selectedCategory
    ? `${currentCategory.name} Articles - WorkoutAIPlan`
    : "Fitness Blog - AI Workouts & Training Guides";

  const seoDescription = selectedCategory
    ? currentCategory.description
    : "Comprehensive fitness blog covering AI workouts, training plans, exercise guides, and personalized fitness strategies for all levels.";

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords="workout plan, fitness blog, AI fitness, training guides, exercise routines, workout routines"
      />

      {/* Dark Background Container */}
      <MKBox sx={{ minHeight: "100vh", background: "#000000", position: "relative" }}>
        {/* Fixed Background */}
        <MKBox
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 0,
            "& .bg-image": {
              position: "absolute",
              inset: 0,
              backgroundImage: 'url(/e7e240850fa17209200eea382be1c90ed7eca601.png)',
              backgroundSize: "cover",
              backgroundPosition: "center 40%",
              backgroundRepeat: "no-repeat",
              filter: "grayscale(100%) contrast(1.25) brightness(1.1)",
            },
            "& .bg-overlay": {
              position: "absolute",
              inset: 0,
              background: "linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.75) 50%, rgba(0,0,0,0.85) 100%)",
            }
          }}
        >
          <MKBox className="bg-image" />
          <MKBox className="bg-overlay" />
        </MKBox>

        {/* Content */}
        <MKBox sx={{ position: "relative", zIndex: 10 }}>
          {/* Header Section */}
          <MKBox
            minHeight="40vh"
            width="100%"
            sx={{
              display: "grid",
              placeItems: "center",
              pt: { xs: 12, md: 15 },
              pb: { xs: 8, md: 10 },
            }}
          >
            <Container>
              <Grid container item xs={12} lg={8} justifyContent="center" mx="auto" textAlign="center">
                <MKTypography
                  variant="h1"
                  mb={2}
                  sx={{
                    color: "#ffffff !important",
                    textShadow: "0 4px 12px rgba(0, 0, 0, 0.8), 0 2px 4px rgba(0, 0, 0, 0.6)",
                    fontFamily: "'Oswald', sans-serif !important",
                    textTransform: "uppercase",
                    letterSpacing: "-0.02em",
                    fontWeight: 700,
                  }}
                >
                  {selectedCategory ? currentCategory.name : "Fitness Blog"}
                </MKTypography>
                <MKTypography
                  variant="h6"
                  px={{ xs: 2, md: 6 }}
                  sx={{
                    color: "rgba(255, 255, 255, 0.8) !important",
                    textShadow: "0 2px 8px rgba(0, 0, 0, 0.8)",
                    fontFamily: "'Oswald', sans-serif !important",
                    fontWeight: 400,
                    letterSpacing: "0.03em",
                  }}
                >
                  {currentCategory.description}
                </MKTypography>
              </Grid>
            </Container>
          </MKBox>

          <Container sx={{ mt: 6 }}>
        {/* Category Filter */}
        <MKBox mb={4}>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              overflowX: "auto",
              pb: 1,
              "&::-webkit-scrollbar": {
                height: 6,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#ccc",
                borderRadius: 3,
              },
            }}
          >
            {categories.map((category) => (
              <MKButton
                key={category.slug || "all"}
                variant={selectedCategory === category.slug ? "contained" : "outlined"}
                onClick={() => handleCategoryChange(category.slug)}
                sx={{
                  minWidth: "fit-content",
                  background: selectedCategory === category.slug ? "#FF6B35" : "transparent",
                  color: selectedCategory === category.slug ? "#ffffff" : "rgba(255, 255, 255, 0.8)",
                  borderColor: selectedCategory === category.slug ? "#FF6B35" : "rgba(255, 255, 255, 0.3)",
                  fontFamily: "'Oswald', sans-serif",
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  "&:hover": {
                    background: selectedCategory === category.slug ? "#E85A2A" : "rgba(255, 107, 53, 0.1)",
                    borderColor: "#FF6B35",
                    color: selectedCategory === category.slug ? "#ffffff" : "#FF6B35",
                  }
                }}
              >
                {category.name}
              </MKButton>
            ))}
          </Stack>
        </MKBox>

        {/* Articles Grid */}
        <Grid container spacing={3} mb={6}>
          {paginatedArticles.map((article) => (
            <Grid item xs={12} sm={6} md={4} key={article.id}>
              <Card
                component={Link}
                to={`/blog/${article.slug}`}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  textDecoration: "none",
                  background: "rgba(255, 255, 255, 0.03)",
                  border: "2px solid rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    borderColor: "rgba(255, 107, 53, 0.5)",
                    background: "rgba(255, 255, 255, 0.05)",
                    boxShadow: "0 12px 24px rgba(255, 107, 53, 0.2)",
                  },
                }}
              >
                <CardMedia
                  component="div"
                  sx={{
                    pt: "56.25%", // 16:9
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  }}
                  image={article.image}
                  title={article.title}
                />
                <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", background: "transparent" }}>
                  <Chip
                    label={article.category.replace("-", " ")}
                    size="small"
                    sx={{
                      mb: 2,
                      textTransform: "capitalize",
                      backgroundColor: "rgba(255, 107, 53, 0.15)",
                      color: "#FF6B35",
                      alignSelf: "flex-start",
                      fontFamily: "'Oswald', sans-serif",
                      fontWeight: 600,
                      letterSpacing: "0.05em",
                      border: "1px solid rgba(255, 107, 53, 0.3)",
                    }}
                  />
                  <MKTypography
                    variant="h5"
                    mb={1}
                    sx={{
                      color: "#ffffff !important",
                      fontFamily: "'Oswald', sans-serif !important",
                      fontWeight: 600,
                      letterSpacing: "0.03em",
                      textTransform: "uppercase",
                    }}
                  >
                    {article.title}
                  </MKTypography>
                  <MKTypography
                    variant="body2"
                    mb={2}
                    sx={{
                      flexGrow: 1,
                      color: "rgba(255, 255, 255, 0.7) !important",
                      fontFamily: "'Oswald', sans-serif !important",
                      fontWeight: 400,
                    }}
                  >
                    {article.excerpt.substring(0, 120)}...
                  </MKTypography>
                  <MKBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mt="auto"
                  >
                    <MKTypography
                      variant="caption"
                      sx={{
                        color: "rgba(255, 255, 255, 0.5) !important",
                        fontFamily: "'Oswald', sans-serif !important",
                        textTransform: "uppercase",
                      }}
                    >
                      {article.readTime}
                    </MKTypography>
                    <MKTypography
                      variant="caption"
                      sx={{
                        color: "rgba(255, 255, 255, 0.5) !important",
                        fontFamily: "'Oswald', sans-serif !important",
                      }}
                    >
                      {new Date(article.publishDate).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </MKTypography>
                  </MKBox>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
              <MKBox display="flex" justifyContent="center" mb={6}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  size="large"
                  showFirstButton
                  showLastButton
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "rgba(255, 255, 255, 0.8)",
                      borderColor: "rgba(255, 255, 255, 0.3)",
                      fontFamily: "'Oswald', sans-serif",
                      "&:hover": {
                        backgroundColor: "rgba(255, 107, 53, 0.1)",
                        borderColor: "#FF6B35",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "#FF6B35",
                        color: "#ffffff",
                        borderColor: "#FF6B35",
                        "&:hover": {
                          backgroundColor: "#E85A2A",
                        }
                      }
                    }
                  }}
                />
              </MKBox>
            )}

            {/* No Results */}
            {filteredArticles.length === 0 && (
              <MKBox textAlign="center" py={8}>
                <MKTypography
                  variant="h4"
                  mb={2}
                  sx={{
                    color: "#ffffff !important",
                    fontFamily: "'Oswald', sans-serif !important",
                    textTransform: "uppercase",
                  }}
                >
                  No articles found
                </MKTypography>
                <MKTypography
                  variant="body1"
                  mb={3}
                  sx={{
                    color: "rgba(255, 255, 255, 0.7) !important",
                    fontFamily: "'Oswald', sans-serif !important",
                  }}
                >
                  Try selecting a different category
                </MKTypography>
                <MKButton
                  onClick={() => handleCategoryChange(null)}
                  sx={{
                    background: "#FF6B35",
                    color: "#ffffff",
                    fontFamily: "'Oswald', sans-serif",
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    "&:hover": {
                      background: "#E85A2A",
                    }
                  }}
                >
                  View All Articles
                </MKButton>
              </MKBox>
            )}
          </Container>
        </MKBox>

        {/* Footer */}
      </MKBox>
    </>
  );
}

export default BlogPage;

